import { cn } from '@/lib/utils';
import { useEffect, useRef } from 'react';

import {
  MARK_PAIRS,
  MARK_SHAPES,
  type Point,
  litPairs,
  morphPoints
} from './signalsShapes';

/**
 * The Signals mark is a graph of nodes. The spinner is that graph: it rotates,
 * the whole group breathes, and the nodes light up in sequence — so a wait
 * looks like work rather than a hang.
 *
 * Replaces the pulsing dot that `.loading-cursor` drew. Keyframes (sig-turn,
 * sig-breathe, sig-node, sig-edge) and the prefers-reduced-motion fallback live
 * in index.css.
 *
 * Three sizes, per the design handoff:
 *
 *   lg — 88px in a 96px box. Empty chat while the first answer computes.
 *   md — 24px in a 26px box. In the thread, in the avatar's place.
 *   sm — 16px in an 18px box. Submit button, composer, refreshing widgets.
 *
 * The graph thins out as it shrinks: 14 edges at lg, 8 at md, and a 4-node
 * diamond at sm, where the hollow ring stops reading and the nodes go solid.
 */

type SpinnerSize = 'lg' | 'md' | 'sm';

const NODES = {
  top: [50, 12],
  ul: [14, 40],
  ur: [86, 40],
  c: [50, 52],
  ll: [26, 88],
  lr: [76, 88]
} as const;

type NodeKey = keyof typeof NODES;

const EDGES_LG: [NodeKey, NodeKey][] = [
  ['top', 'ul'],
  ['top', 'ur'],
  ['top', 'c'],
  ['top', 'll'],
  ['top', 'lr'],
  ['ul', 'c'],
  ['ur', 'c'],
  ['ul', 'll'],
  ['ur', 'lr'],
  ['ul', 'lr'],
  ['ur', 'll'],
  ['ll', 'c'],
  ['lr', 'c'],
  ['ll', 'lr']
];

// At 24px the long crossings and the outer spokes turn to mush; keep the star
// through the centre plus the base.
const EDGES_MD: [NodeKey, NodeKey][] = [
  ['top', 'ul'],
  ['top', 'ur'],
  ['top', 'c'],
  ['ul', 'c'],
  ['ur', 'c'],
  ['ll', 'c'],
  ['lr', 'c'],
  ['ll', 'lr']
];

// A separate, wider geometry: at 16px the six-node graph reads as a smudge.
const SM_NODES: [number, number][] = [
  [50, 14],
  [16, 42],
  [84, 42],
  [50, 86]
];
const SM_EDGES: [number, number][] = [
  [0, 1],
  [0, 2],
  [1, 3],
  [2, 3]
];

const SPEC: Record<
  SpinnerSize,
  {
    box: number;
    svg: number;
    edgeWidth: number;
    nodeStroke: number;
    nodeR: number;
    nodeStagger: number;
    edgeStagger: number;
    color: string;
    solid: boolean;
  }
> = {
  lg: {
    box: 96,
    svg: 88,
    edgeWidth: 1.6,
    nodeStroke: 2.6,
    nodeR: 6.5,
    nodeStagger: 0.28,
    edgeStagger: 0.1,
    color: 'var(--sig-node)',
    solid: false
  },
  md: {
    box: 26,
    svg: 24,
    edgeWidth: 3,
    nodeStroke: 5,
    nodeR: 8,
    nodeStagger: 0.3,
    edgeStagger: 0.2,
    color: 'var(--sig-node)',
    solid: false
  },
  sm: {
    box: 18,
    svg: 16,
    edgeWidth: 4,
    nodeStroke: 0,
    nodeR: 11,
    nodeStagger: 0.4,
    edgeStagger: 0.3,
    // The node blue is too light to survive at 16px; the BuduCloud blue holds.
    color: 'var(--sig-blue)',
    solid: true
  }
};

interface Props {
  size?: SpinnerSize;
  className?: string;
  /** Announced to screen readers. Falsy renders the mark as decorative. */
  label?: string;
  /**
   * Move through the shapes in signalsShapes instead of turning: the graph
   * becomes a ring, a triangle, a rising line and a hub, one per beat. For
   * the long wait on an answer, where a mark that only turns looks the same
   * at second 3 and at second 40. Ignored at sm, where the six-node graph is
   * not drawn at all.
   */
  morph?: boolean;
}

/** How long the nodes take to travel to the next shape; the beat is 2.6s. */
const MORPH_MS = 900;
const BEAT_MS = 2600;

// Every morph starts from the logo itself.
const FIRST = MARK_SHAPES[0];
const FIRST_LIT = litPairs(FIRST);

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * The six nodes are moved by writing their attributes directly, not through
 * React state: a re-render 60 times a second for a spinner would be the most
 * expensive thing on the page. The frames only run for the 0.9s of travel in
 * each 2.6s beat. Lines are not CSS-animatable (x1/y1 are attributes), which
 * is why this is not a transition.
 */
const MorphingMark = ({
  spec,
  size,
  label
}: {
  spec: (typeof SPEC)[SpinnerSize];
  size: SpinnerSize;
  label?: string;
}) => {
  const groups = useRef<(SVGGElement | null)[]>([]);
  const lines = useRef<(SVGLineElement | null)[]>([]);
  const nodes = useRef<(SVGCircleElement | null)[]>([]);

  useEffect(() => {
    const draw = (pts: Point[]) => {
      MARK_PAIRS.forEach(([a, b], i) => {
        const line = lines.current[i];
        if (!line) return;
        line.setAttribute('x1', String(pts[a][0]));
        line.setAttribute('y1', String(pts[a][1]));
        line.setAttribute('x2', String(pts[b][0]));
        line.setAttribute('y2', String(pts[b][1]));
      });
      pts.forEach(([x, y], i) => {
        const node = nodes.current[i];
        if (!node) return;
        node.setAttribute('cx', String(x));
        node.setAttribute('cy', String(y));
      });
    };
    if (prefersReducedMotion()) return;

    let index = 0;
    let current: Point[] = FIRST.points.map(([x, y]) => [x, y]);
    let from = current;
    let to = current;
    let travelStart = 0;
    let frame = 0;
    const step = (now: number) => {
      const t = (now - travelStart) / MORPH_MS;
      current = morphPoints(from, to, t);
      draw(current);
      if (t < 1) frame = requestAnimationFrame(step);
    };
    const beat = window.setInterval(() => {
      index = (index + 1) % MARK_SHAPES.length;
      const shape = MARK_SHAPES[index];
      const lit = litPairs(shape);
      groups.current.forEach((g, i) => {
        if (g) g.style.opacity = lit[i] ? '1' : '0';
      });
      from = current;
      to = shape.points;
      travelStart = performance.now();
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(step);
    }, BEAT_MS);
    return () => {
      window.clearInterval(beat);
      cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <span
      className="inline-flex items-center justify-center"
      style={{
        width: spec.box,
        height: spec.box,
        animation: 'sig-breathe 2.6s ease-in-out infinite'
      }}
      role={label ? 'status' : undefined}
      aria-label={label || undefined}
      aria-hidden={label ? undefined : true}
      data-morph={size}
    >
      <svg
        className="sig-spinner"
        width={spec.svg}
        height={spec.svg}
        viewBox="0 0 100 100"
      >
        <g
          stroke={spec.color}
          strokeWidth={spec.edgeWidth}
          strokeLinecap="round"
          fill="none"
        >
          {MARK_PAIRS.map(([a, b], i) => (
            <g
              key={`${a}-${b}`}
              ref={(el) => {
                groups.current[i] = el;
              }}
              style={{
                opacity: FIRST_LIT[i] ? 1 : 0,
                transition: 'opacity 0.7s ease-in-out'
              }}
            >
              <line
                ref={(el) => {
                  lines.current[i] = el;
                }}
                x1={FIRST.points[a][0]}
                y1={FIRST.points[a][1]}
                x2={FIRST.points[b][0]}
                y2={FIRST.points[b][1]}
                style={{
                  animation: `sig-edge 2.6s ease-in-out ${(
                    (i % 8) *
                    spec.edgeStagger
                  ).toFixed(2)}s infinite`
                }}
              />
            </g>
          ))}
        </g>
        <g fill="#fff" stroke={spec.color} strokeWidth={spec.nodeStroke}>
          {FIRST.points.map(([cx, cy], i) => (
            <circle
              key={i}
              ref={(el) => {
                nodes.current[i] = el;
              }}
              cx={cx}
              cy={cy}
              r={spec.nodeR}
              style={{
                animation: `sig-node 2.6s ease-in-out ${(
                  i * spec.nodeStagger
                ).toFixed(2)}s infinite`
              }}
            />
          ))}
        </g>
      </svg>
    </span>
  );
};

export const SignalsSpinner = ({
  size = 'md',
  className,
  label,
  morph
}: Props) => {
  const spec = SPEC[size];
  if (morph && !spec.solid) {
    return (
      <span className={cn('inline-flex', className)}>
        <MorphingMark spec={spec} size={size} label={label} />
      </span>
    );
  }
  const points: [number, number][] = spec.solid
    ? SM_NODES
    : (Object.keys(NODES) as NodeKey[]).map((k) => [NODES[k][0], NODES[k][1]]);
  const lines: [[number, number], [number, number]][] = spec.solid
    ? SM_EDGES.map(([a, b]) => [SM_NODES[a], SM_NODES[b]])
    : (size === 'lg' ? EDGES_LG : EDGES_MD).map(([a, b]) => [
        [NODES[a][0], NODES[a][1]],
        [NODES[b][0], NODES[b][1]]
      ]);

  return (
    <span
      className={cn('inline-flex items-center justify-center', className)}
      style={{
        width: spec.box,
        height: spec.box,
        animation: 'sig-breathe 2.6s ease-in-out infinite'
      }}
      role={label ? 'status' : undefined}
      aria-label={label || undefined}
      aria-hidden={label ? undefined : true}
    >
      <svg
        className="sig-spinner"
        width={spec.svg}
        height={spec.svg}
        viewBox="0 0 100 100"
        style={{
          animation: 'sig-turn 9s linear infinite',
          transformOrigin: '50% 50%'
        }}
      >
        <g
          stroke={spec.color}
          strokeWidth={spec.edgeWidth}
          strokeLinecap="round"
          fill="none"
        >
          {lines.map(([[x1, y1], [x2, y2]], i) => (
            <line
              key={`${x1}-${y1}-${x2}-${y2}`}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              style={{
                animation: `sig-edge 2.6s ease-in-out ${(
                  i * spec.edgeStagger
                ).toFixed(2)}s infinite`
              }}
            />
          ))}
        </g>
        <g
          fill={spec.solid ? spec.color : '#fff'}
          stroke={spec.solid ? undefined : spec.color}
          strokeWidth={spec.solid ? undefined : spec.nodeStroke}
        >
          {points.map(([cx, cy], i) => (
            <circle
              key={`${cx}-${cy}`}
              cx={cx}
              cy={cy}
              r={spec.nodeR}
              style={{
                animation: `sig-node 2.6s ease-in-out ${(
                  i * spec.nodeStagger
                ).toFixed(2)}s infinite`
              }}
            />
          ))}
        </g>
      </svg>
    </span>
  );
};

/**
 * The 88px state with its two lines of copy. The first line should name the step
 * the backend is actually on; the second sets an expectation, because an ERP
 * question that reads four tables is not instant and saying so beats a bare
 * spinner.
 */
export const SignalsThinking = ({
  title,
  detail,
  className
}: {
  title: string;
  detail?: string;
  className?: string;
}) => (
  <div
    className={cn('flex flex-col items-center gap-[22px]', className)}
    role="status"
  >
    <SignalsSpinner size="lg" />
    <div className="text-center">
      <div
        className="text-[15px] font-semibold"
        style={{ color: 'var(--sig-ink)' }}
        dir="auto"
      >
        {title}
      </div>
      {detail ? (
        <div
          className="text-[12.5px] mt-1"
          style={{ color: 'var(--sig-muted)' }}
          dir="auto"
        >
          {detail}
        </div>
      ) : null}
    </div>
  </div>
);
